import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X, Trash2, ArrowRight, GitBranch } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { isPlanGateError } from '@/lib/planGate';
import { orgChangeApi, dimensionsApi, OrgChange, OrgChangeInput, ChangeType, DimensionWithMembers } from '@/lib/orgChange';

const today = new Date().toISOString().slice(0, 10);
const CHANGE_TYPES: ChangeType[] = ['move', 'merge', 'split', 'rename'];

export function OrgChangesPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const changes = useQuery({ queryKey: ['org-changes', wsId], queryFn: () => orgChangeApi.list(wsId!), enabled: !!wsId, retry: false });
  const planGated = changes.error && isPlanGateError(changes.error);

  return (
    <AppShell title={t('org_changes.title', { defaultValue: 'Org changes' })} subtitle={t('org_changes.subtitle', { defaultValue: 'Time-versioned department structure for comparable history.' })}>
      <main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('org_changes.title', { defaultValue: 'Org changes' })} <span className="text-muted-foreground text-lg">組織変更</span></h1>
            <p className="mt-1 text-ink-600">{t('org_changes.subtitle', { defaultValue: 'Record a re-org (merge / split / rename a department) effective from a date. Reports can then restate history into the current structure so budget-vs-actual stays comparable.' })}</p>
          </div>
          {!planGated && <button onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"><Plus className="h-4 w-4" /> {t('org_changes.new', { defaultValue: 'New org change' })}</button>}
        </div>

        {planGated && wsId && <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('org_changes.feature_name', { defaultValue: 'Org-change handling' })} />}

        {showForm && wsId && <NewOrgChangeForm wsId={wsId} onClose={() => setShowForm(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['org-changes', wsId] }); setShowForm(false); }} t={t} />}

        {!planGated && (
          changes.isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            : (changes.data ?? []).length === 0 ? (
              <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
                <GitBranch className="h-6 w-6 text-ink-300 mx-auto mb-2" />
                <p className="text-ink-700 font-medium">{t('org_changes.empty', { defaultValue: 'No org changes recorded.' })}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(changes.data ?? []).map((c) => <OrgChangeRow key={c.id} wsId={wsId!} c={c} t={t} />)}
              </div>
            )
        )}
      </main>
    </AppShell>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

function OrgChangeRow({ wsId, c, t }: { wsId: string; c: OrgChange; t: TFn }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const del = useMutation({ mutationFn: () => orgChangeApi.remove(wsId, c.id), onSuccess: () => qc.invalidateQueries({ queryKey: ['org-changes', wsId] }) });
  return (
    <div className="rounded-md border border-border bg-white px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-ink-900 truncate">{c.name}</p>
          <p className="text-[12px] text-muted-foreground num mt-0.5">
            <span className="uppercase tracking-wide rounded bg-ink-100 px-1.5 py-0.5 text-[10px] font-semibold">{c.change_type}</span>
            {' '}· {c.dimension_key} · {t('org_changes.effective', { defaultValue: 'effective' })} {c.effective_from}
          </p>
        </div>
        {!confirm ? (
          <button onClick={() => setConfirm(true)} className="text-ink-400 hover:text-warn-600 p-1 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
        ) : (
          <span className="inline-flex items-center gap-2 text-xs shrink-0">
            <button onClick={() => del.mutate()} disabled={del.isPending} className="text-warn-700 font-semibold hover:underline">{del.isPending ? t('common.loading') : t('common.yes')}</button>
            <button onClick={() => setConfirm(false)} className="text-ink-500 hover:underline">{t('common.no')}</button>
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {c.mappings.map((m) => (
          <span key={m.id} className="inline-flex items-center gap-1 text-[11px] num rounded-full border border-border bg-ink-50/60 px-2 py-0.5">
            {m.from_label || m.from_code} <ArrowRight className="h-3 w-3 text-emerald-600" /> {m.to_label || m.to_code}
          </span>
        ))}
      </div>
    </div>
  );
}

function NewOrgChangeForm({ wsId, onClose, onDone, t }: { wsId: string; onClose: () => void; onDone: () => void; t: TFn }) {
  const { data: dims = [] } = useQuery({ queryKey: ['dimensions', wsId], queryFn: () => dimensionsApi.list(wsId) });
  const [name, setName] = useState('');
  const [dimensionId, setDimensionId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [changeType, setChangeType] = useState<ChangeType>('merge');
  const [rows, setRows] = useState<{ fromMemberId: string; toMemberId: string }[]>([{ fromMemberId: '', toMemberId: '' }]);
  const [err, setErr] = useState<string | null>(null);
  const input = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm';

  const dim = useMemo<DimensionWithMembers | undefined>(() => dims.find((d) => d.id === dimensionId), [dims, dimensionId]);
  const members = dim?.members ?? [];

  const save = useMutation({
    mutationFn: async () => {
      const mappings = rows.filter((r) => r.fromMemberId && r.toMemberId);
      const body: OrgChangeInput = { name: name.trim(), dimensionId, effectiveFrom, changeType, mappings };
      return orgChangeApi.create(wsId, body);
    },
    onSuccess: onDone,
    onError: (e) => setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? (e as Error).message),
  });

  const noDims = dims.length === 0;

  return (
    <div className="rounded-md border border-border bg-white p-5 space-y-4">
      <h3 className="text-sm font-semibold text-ink-700">{t('org_changes.new', { defaultValue: 'New org change' })}</h3>
      {noDims && <p className="text-[13px] text-warn-700">{t('org_changes.no_dims', { defaultValue: 'No dimensions defined yet. Define a dimension (e.g. dept) with members first — uploads with a dept column create these automatically.' })}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Lbl label={t('org_changes.name', { defaultValue: 'Name' })}><input value={name} onChange={(e) => setName(e.target.value)} className={input} placeholder="FY26 Sales→Operations merge" /></Lbl>
        <Lbl label={t('org_changes.dimension', { defaultValue: 'Dimension' })}>
          <select value={dimensionId} onChange={(e) => { setDimensionId(e.target.value); setRows([{ fromMemberId: '', toMemberId: '' }]); }} className={input}>
            <option value="">{t('org_changes.pick_dim', { defaultValue: 'Select dimension…' })}</option>
            {dims.map((d) => <option key={d.id} value={d.id}>{d.label || d.key} ({d.key})</option>)}
          </select>
        </Lbl>
        <Lbl label={t('org_changes.effective_date', { defaultValue: 'Effective from' })}><input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className={`num ${input}`} /></Lbl>
        <Lbl label={t('org_changes.change_type', { defaultValue: 'Change type' })}>
          <select value={changeType} onChange={(e) => setChangeType(e.target.value as ChangeType)} className={input}>
            {CHANGE_TYPES.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
          </select>
        </Lbl>
      </div>

      <div>
        <span className="block text-xs font-semibold text-ink-700 mb-1">{t('org_changes.mappings', { defaultValue: 'Member remappings (from → to)' })}</span>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <select value={r.fromMemberId} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, fromMemberId: e.target.value } : x))} className={input} disabled={!dim}>
                <option value="">{t('org_changes.source_member', { defaultValue: 'source…' })}</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.label || m.code}</option>)}
              </select>
              <ArrowRight className="h-4 w-4 text-emerald-600 shrink-0" />
              <select value={r.toMemberId} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, toMemberId: e.target.value } : x))} className={input} disabled={!dim}>
                <option value="">{t('org_changes.target_member', { defaultValue: 'target…' })}</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.label || m.code}</option>)}
              </select>
              <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} className="text-ink-400 hover:text-warn-600 p-1 shrink-0"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => setRows((rs) => [...rs, { fromMemberId: '', toMemberId: '' }])} className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> {t('org_changes.add_mapping', { defaultValue: 'Add remapping' })}</button>
        </div>
      </div>

      {err && <p className="text-xs text-warn-700">{err}</p>}
      <div className="flex gap-2">
        <button onClick={() => save.mutate()} disabled={save.isPending || !name.trim() || !dimensionId} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">{save.isPending ? t('common.loading') : t('org_changes.create', { defaultValue: 'Create' })}</button>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]">{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{label}</span>{children}</label>;
}
